import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Clock, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import i18n from "@/lib/i18n";

import { tAuto } from "@/lib/tAuto";
export type PaymentReferenceNotification = {
  id: string;
  data?: any;
};

interface Props {
  notification: PaymentReferenceNotification | null;
  onClose: () => void;
}

export const PaymentReferenceModal = ({ notification, onClose }: Props) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!notification) return null;
  const d = notification.data || {};
  const entity = String(d.entity || "");
  const reference = String(d.reference || "");
  const amount = Number(d.amount || 0);
  const amountFmt = `${amount.toLocaleString("pt-AO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kz`;
  const refFmt = (() => {
    const digits = reference.replace(/\D/g, "");
    if (digits.length === 9) return digits.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
    return reference;
  })();
  const expiresAt = d.expires_at ? new Date(d.expires_at) : null;
  const timeLeft = (() => {
    if (!expiresAt) return null;
    const diff = expiresAt.getTime() - now.getTime();
    if (diff <= 0) return "Expirado";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (h > 0) return `${h}h ${m}m`;
    const s = Math.floor((diff % 60000) / 1000);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  })();

  const copy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(field);
      toast.success(`${field} ${i18n.t("notifications.copied") || "copiado"}`);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const Field = ({ label, value, copyValue }: { label: string; value: string; copyValue: string }) => (
    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-muted/30 border border-border/40">
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-[16px] font-bold text-foreground truncate tabular-nums">{value}</p>
      </div>
      <button
        type="button"
        onClick={() => copy(copyValue, label)}
        className="shrink-0 w-9 h-9 rounded-xl bg-background border border-border/50 flex items-center justify-center hover:bg-primary/10 hover:border-primary/40 active:scale-95 transition-all"
        aria-label={`Copiar ${label}`}
      >
        {copied === label ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
      </button>
    </div>
  );

  return (
    <Dialog open={!!notification} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="p-0 border-border/40 shadow-2xl rounded-2xl overflow-hidden z-[10060] w-[440px] max-w-[92vw]">
        <div className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#0f3460] px-5 py-3.5 flex items-center gap-2">
          <FileText className="w-4 h-4 text-white" />
          <span className="font-bold text-[13px] text-white">{tAuto("auto.components_notifications_paymentreferencemodal.1", "Pagamento por Referência")}</span>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="text-center space-y-2">
            <div className="inline-flex w-12 h-12 rounded-2xl bg-primary/10 items-center justify-center mb-1">
              <FileText className="w-6 h-6 text-primary" strokeWidth={1.8} />
            </div>
            <p className="text-[13px] text-muted-foreground leading-relaxed px-2">
              Use os dados abaixo no <strong>ATM Multicaixa</strong> ou no <strong>Internet Banking</strong>. Nome da Entidade:
            </p>
            <div className="mx-auto inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-primary/12 via-primary/8 to-primary/12 border border-primary/25 shadow-sm">
              <span className="text-[15px] font-extrabold tracking-[0.12em] bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                PAY4ALL
              </span>
            </div>
          </div>

          {timeLeft && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200/60 text-amber-800">
              <Clock className="w-4 h-4 shrink-0" />
              <span className="text-[12px] font-semibold">
                {timeLeft === "Expirado" ? "Referência expirada" : <>{tAuto("auto.components_notifications_paymentreferencemodal.2", "Válido por")} <span className="tabular-nums">{timeLeft}</span></>}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-br from-primary/8 to-primary/4 border-2 border-primary/20">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-primary/80">Entidade</p>
              <p className="text-[20px] font-extrabold text-foreground tracking-[0.08em] leading-tight">{entity}</p>
            </div>
            <button
              type="button"
              onClick={() => copy(entity, "Entidade")}
              className="shrink-0 w-9 h-9 rounded-xl bg-background border border-primary/30 flex items-center justify-center hover:bg-primary/10 active:scale-95 transition-all"
              aria-label="Copiar Entidade"
            >
              {copied === "Entidade" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-primary" />}
            </button>
          </div>

          <div className="space-y-2.5">
            <Field label={tAuto("auto.components_notifications_paymentreferencemodal.3", "Referência")} value={refFmt} copyValue={reference} />
            <Field label="Valor" value={amountFmt} copyValue={String(amount)} />
          </div>

          {d.property_title && (
            <p className="text-[11px] text-center text-muted-foreground/70 leading-snug">
              Reserva: <strong className="text-foreground/80">{String(d.property_title)}</strong>
            </p>
          )}

          <Button onClick={onClose} variant="ghost" size="sm" className="w-full h-10 text-sm font-semibold text-muted-foreground hover:text-foreground">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentReferenceModal;
